import React, { useEffect, useState } from 'react';
import { TextField } from '@mui/material';
import { useField, useFormikContext } from 'formik';

export const PercentTextField = React.memo((props) => {
  const {
    label,
    loading,
    disabled,
    useFormik = false,
    helperText,
    value: propValue,
    onBlur: propOnBlur,
    onChange,
    error = false,
    isSetting = false,
    allowDecimal = false, // Default to false
    allowZero = false,
    decimalPlaces = 2,
    disableBlurFormat = false, // Optional: disable formatting on blur
    allowIntermediateDecimal = false, // Optional: allow intermediate decimal states like "4."
    ...otherProps
  } = props;

  let field, meta, setFieldValue;
  let formikValue = 0;

  if (useFormik) {
    [field, meta] = useField(props);
    const formik = useFormikContext();
    setFieldValue = formik?.setFieldValue;
    formikValue = field?.value || 0;
  }

  const initialValue = useFormik ? formikValue : propValue || 0;

  const formatValue = (value) => {
    const valueInPercent = value * 100;
    return allowDecimal
      ? valueInPercent.toFixed(decimalPlaces).replace(/\.?0+$/, '') // Remove trailing zeros if not needed
      : Math.round(valueInPercent).toString();
  };

  const [currentFieldValue, setCurrentFieldValue] = useState(
    initialValue === 0 && !allowZero ? '' : formatValue(initialValue),
  );
  useEffect(() => {
    const externalValue = useFormik ? formikValue : propValue || 0;
    const formattedExternal = externalValue === 0 && !isSetting ? '' : formatValue(externalValue);
    // Only update if the value actually changed from external source
    // Skip update if we're allowing intermediate decimals and current value ends with '.'
    if (allowIntermediateDecimal && currentFieldValue.endsWith('.')) {
      return;
    }
    if (formattedExternal !== currentFieldValue) {
      setCurrentFieldValue(formattedExternal);
    }
  }, [formikValue, propValue, useFormik, isSetting]);

  const handleChange = (evt) => {
    let inputValue = evt.target.value;

    // Remove the % sign and allow only valid characters
    inputValue = inputValue.replace('%', '').replace(
      allowDecimal ? /[^0-9.-]/g : /[^0-9]/g, // Allow `-` only if `allowDecimal` is true
      '',
    );

    // Prevent multiple dots or misplaced negative signs
    if (allowDecimal) {
      if ((inputValue.match(/\./g) || []).length > 1) {
        return;
      }
      if ((inputValue.match(/-/g) || []).length > 1 || inputValue.indexOf('-') > 0) {
        return;
      }
    }

    // Restrict to two digits before the decimal point
    const parts = inputValue.split('.');
    if (parts[0].replace('-', '').length > 2) {
      return;
    }

    // Restrict decimal places
    const maxDecimals = decimalPlaces || 2;
    if (allowDecimal && parts[1] && parts[1].length > maxDecimals) {
      return;
    }

    if (allowDecimal && parts.length > 1 && parts[1].length > decimalPlaces) {
      return;
    }

    // Update the local state
    setCurrentFieldValue(inputValue);

    // Update Formik or external handler
    // Add 2 to decimalPlaces to preserve precision when converting percentage to decimal (e.g., 12.5555% -> 0.125555)
    if (useFormik && setFieldValue) {
      if (inputValue === '-' || inputValue === '') {
        // Allow intermediate states
      } else {
        let parsedValue = parseFloat(inputValue) / 100 || 0;
        parsedValue = Number(parsedValue.toFixed(6));
        setFieldValue(field.name, parsedValue);
      }
    } else if (onChange) {
      if (inputValue !== '-' && inputValue !== '') {
        let parsedValue = parseFloat(inputValue) / 100 || 0;
        parsedValue = Number(parsedValue.toFixed(6));
        onChange({ name: props.name, value: parsedValue });
      }
    }
  };

  const handleBlur = (evt) => {
    // Allow empty values
    if (currentFieldValue === '' || currentFieldValue === '.') {
      if (useFormik && setFieldValue) {
        setFieldValue(field.name, '');
      } else if (onChange) {
        onChange({ name: props.name, value: '' });
      }
      if (propOnBlur) {
        propOnBlur(evt);
      }
      return;
    }

    const numericValue = parseFloat(currentFieldValue) || 0;

    if (!disableBlurFormat) {
      setCurrentFieldValue(
        allowDecimal
          ? numericValue.toFixed(decimalPlaces).replace(/\.?0+$/, '') // Remove trailing zeros
          : Math.round(numericValue).toString(),
      );
    }

    if (useFormik && setFieldValue) {
      setFieldValue(field.name, numericValue / 100);
    } else if (onChange) {
      onChange({ name: props.name, value: numericValue / 100 });
    }
    if (propOnBlur) {
      propOnBlur(evt);
    }
  };

  return (
    <TextField
      {...(useFormik ? field : {})}
      {...otherProps}
      label={label || 'Label'}
      value={loading ? 'Loading...' : currentFieldValue}
      onChange={!disabled ? handleChange : undefined}
      onBlur={!disabled ? handleBlur : undefined}
      error={useFormik ? meta?.touched && Boolean(meta.error) : error}
      helperText={useFormik ? (meta?.touched && meta?.error ? meta.error : helperText) : helperText}
      disabled={disabled}
      inputProps={{
        maxLength: allowDecimal ? (decimalPlaces === 4 ? 8 : 6) : 3,
      }}
      InputProps={{
        endAdornment: <span>%</span>,
      }}
    />
  );
});

PercentTextField.displayName = 'PercentTextField';
